import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await api.get('/user/dashboard');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const cancelAppointment = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this appointment?')) return;
    try {
      await api.delete(`/appointments/${id}`);
      loadDashboard();
    } catch (error) {
      alert('Failed to cancel appointment. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-tertiary flex items-center justify-center">
        <div className="text-primary text-xl">Loading your dashboard...</div>
      </div>
    );
  }

  const { summary, upcoming, past } = dashboardData || { summary: {}, upcoming: [], past: [] };

  return (
    <div className="min-h-screen bg-gradient-to-br from-tertiary via-neutral-warm to-tertiary py-12">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-light text-primary mb-2">
            Welcome back, {user?.firstName}!
          </h1>
          <p className="text-primary-light">Manage your appointments and profile</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white rounded-2xl p-6 shadow-lg border-l-4 border-accent">
            <div className="text-accent text-3xl font-bold mb-1">{summary.totalAppointments || 0}</div>
            <div className="text-primary text-sm">Total Appointments</div>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-lg border-l-4 border-primary">
            <div className="text-primary text-3xl font-bold mb-1">{summary.upcomingCount || 0}</div>
            <div className="text-primary text-sm">Upcoming Sessions</div>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-lg border-l-4 border-accent-light">
            <div className="text-accent-dark text-lg font-medium mb-1">
              {summary.nextAppointment ? (
                format(new Date(summary.nextAppointment.appointment_date), 'MMM d, yyyy')
              ) : (
                'None scheduled'
              )}
            </div>
            <div className="text-primary text-sm">Next Appointment</div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-12">
          <button
            onClick={() => navigate('/')}
            className="bg-gradient-to-r from-accent to-accent-dark text-white px-8 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
          >
            Book New Appointment
          </button>
        </div>

        {/* Upcoming Appointments */}
        <div className="mb-12">
          <h2 className="text-3xl font-light text-primary mb-6 flex items-center">
            <span className="w-12 h-1 bg-accent mr-4 rounded"></span>
            Upcoming Appointments
          </h2>
          {upcoming.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center shadow-lg">
              <div className="text-6xl mb-4">📅</div>
              <p className="text-primary text-lg mb-4">No upcoming appointments</p>
              <p className="text-primary-light mb-6">Book your next session to continue your hair-free journey!</p>
              <button
                onClick={() => navigate('/')}
                className="bg-accent text-white px-8 py-3 rounded-full font-semibold hover:bg-accent-dark transition-all"
              >
                Book Now
              </button>
            </div>
          ) : (
            <div className="grid gap-6">
              {upcoming.map((apt) => (
                <div
                  key={apt.id}
                  className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-accent"
                >
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                    <div className="flex-1">
                      <div className="flex items-center mb-3">
                        <div className="text-2xl font-bold text-primary mr-4">
                          {format(new Date(apt.appointmentDate), 'd')}
                        </div>
                        <div>
                          <div className="text-lg text-primary font-medium">
                            {format(new Date(apt.appointmentDate), 'EEEE, MMMM yyyy')}
                          </div>
                          <div className="text-accent font-semibold">{apt.startTime}</div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-primary-light">
                        <span>⏱️ {apt.durationMinutes} minutes</span>
                        <span>💵 ${apt.price}</span>
                        {apt.paid && <span className="text-green-600 font-medium">✓ Paid</span>}
                      </div>
                      {apt.notes && (
                        <p className="mt-3 text-sm text-primary bg-tertiary p-3 rounded-lg">
                          Note: {apt.notes}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => cancelAppointment(apt.id)}
                      className="mt-4 md:mt-0 md:ml-6 text-red-600 hover:text-red-800 hover:bg-red-50 px-4 py-2 rounded-lg transition-all"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Past Appointments */}
        {past && past.length > 0 && (
          <div>
            <h2 className="text-3xl font-light text-primary mb-6 flex items-center">
              <span className="w-12 h-1 bg-primary-light mr-4 rounded"></span>
              Past Appointments
            </h2>
            <div className="grid gap-4">
              {past.map((apt) => (
                <div
                  key={apt.id}
                  className="bg-white rounded-xl p-4 shadow-sm opacity-75 hover:opacity-100 transition-opacity"
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="text-primary font-medium">
                        {format(new Date(apt.appointmentDate), 'MMM d, yyyy')}
                      </span>
                      <span className="text-primary-light mx-2">•</span>
                      <span className="text-primary-light">{apt.startTime}</span>
                      <span className="text-primary-light mx-2">•</span>
                      <span className="text-sm text-primary-light">{apt.durationMinutes} min</span>
                    </div>
                    <span className="text-sm px-3 py-1 rounded-full bg-tertiary text-primary capitalize">
                      {apt.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
